import { apiClient } from 'axios-client';
import { useCurrentBilikPerson } from 'hooks/use-current-bilik-person/use-current-bilik-person';
import React, { FunctionComponent, useCallback, useState } from 'react';
import { Button, Icon, Popup } from 'semantic-ui-react';
import { ToastError, ToastWarn } from 'utils';
import { useReviewLocker } from '../hooks/useReviewLocker';

type ModerateReviewReplyActionsProps = {
  reviewId: number;
  reviewStatus: string;
  reply?: string;
  replyStatus?: string;
  onModerateSuccess?: (type: 'validate' | 'refuse') => void;
  disabled?: boolean;
};

export const ModerateReviewReplyActions: FunctionComponent<
  ModerateReviewReplyActionsProps
> = ({
  reviewId,
  reply,
  replyStatus,
  reviewStatus,
  onModerateSuccess,
  disabled,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { currentBilikPerson } = useCurrentBilikPerson();
  const { unlock, checkLocker } = useReviewLocker(reviewId);

  const moderateReply = useCallback(
    async (reviewId: number, type: 'validate' | 'refuse', reply?: string) => {
      if (reviewStatus !== 'ok') {
        ToastWarn(
          'Loupé',
          "Veuillez d'abord modérer l'avis avant de modérer la réponse (ceci afin d'être cohérent dans l'envoi des mails aux parties).",
        );
        return;
      }

      setIsLoading(true);
      try {
        const locker = await checkLocker();
        if (locker && locker.id !== currentBilikPerson?.id) {
          ToastError(
            'Erreur',
            `Avis verrouillé par ${locker.givenName} ${locker.familyName}, impossible de modérer la réponse.`,
          );
          return;
        }

        await apiClient.post(`/form/review-pro/reply-moderate`, {
          proReviewId: reviewId,
          type,
          reply,
        });
        await unlock();
      } finally {
        setIsLoading(false);
      }
      if (onModerateSuccess) onModerateSuccess(type);
    },
    [
      reviewStatus,
      onModerateSuccess,
      unlock,
      checkLocker,
      currentBilikPerson?.id,
    ],
  );

  if (!replyStatus) return null;

  return (
    <>
      {replyStatus !== 'ok' ? (
        <Popup
          content={
            reviewStatus === 'ok'
              ? 'Valider (déclenche des mails)'
              : "Veuillez d'abord modérer l'avis avant de modérer la réponse"
          }
          trigger={
            <span>
              <Button
                positive
                basic
                loading={isLoading}
                disabled={isLoading || disabled || reviewStatus !== 'ok'}
                onClick={async () => {
                  await moderateReply(reviewId, 'validate', reply);
                }}
              >
                <Icon name="checkmark" />
                Valider
              </Button>
            </span>
          }
        />
      ) : null}
      {replyStatus !== 'ko' ? (
        <Popup
          content="Refuser (déclenche un mail vers le pro)"
          trigger={
            <Button
              negative
              basic
              loading={isLoading}
              disabled={isLoading || disabled || reviewStatus !== 'ok'}
              onClick={async () =>
                await moderateReply(reviewId, 'refuse', reply)
              }
            >
              <Icon name="remove" />
              Refuser
            </Button>
          }
        />
      ) : null}
    </>
  );
};
